/**
 * WB4 OD4-A — Evidence validation state SSOT (required · missing · attached).
 * Derived from kernel action metadata, server auxiliary summary, or exception rows — never synthetic success.
 */

import type { WorkbenchException } from './exception-registry';
import type { WorkbenchActionExecutionEntry } from './workbench-action-execution-log';
import type { WorkbenchAuxiliarySummary } from './workbench-timeline-event';

/** Cockpit pilots enrolled for D8 evidence validation density (Wave 4 OD4-A · Wave 5 OD5-A/B tail). */
export const EVIDENCE_VALIDATION_PRIORITY_PILOT_KEYS = [
  'order',
  'finance',
  'warehouse_picking',
  'finance_collection',
  'entity_customer',
  'entity_supplier',
  'knowledge_document',
  'inventory_shortage',
  'task_execution',
] as const;

export type WorkbenchEvidenceAttachmentState = 'required' | 'missing' | 'attached';

export interface WorkbenchEvidenceStateMetadata {
  state: WorkbenchEvidenceAttachmentState;
  label: string;
  detail?: string;
  requirementCount?: number;
  attachedCount?: number;
}

export interface AuxiliaryEvidenceDisplayState {
  requiredCount: number;
  missingCount: number;
  attachedCount: number;
  /** True when at least one count comes from server summary or action metadata. */
  serverBacked: boolean;
}

export function resolveEvidenceStateFromKernelAction(input: {
  actionId: string;
  handlerRef: string;
}): WorkbenchEvidenceStateMetadata | undefined {
  const { actionId, handlerRef } = input;

  if (
    handlerRef === 'document:attach_evidence' ||
    actionId === 'reference:attach_evidence' ||
    actionId === 'drag_drop:attach_evidence'
  ) {
    return {
      state: 'attached',
      label: '증빙 첨부됨',
      detail: '승인 패킷 증빙 흐름이 연결되었습니다.',
      attachedCount: 1,
    };
  }

  if (
    handlerRef === 'workbench:intent:collect_payment' ||
    actionId === 'customer:open_collection_thread' ||
    actionId === 'collection:open_follow_up_thread' ||
    actionId === 'finance:collect'
  ) {
    return {
      state: 'required',
      label: '수금 증빙 필요',
      detail: '후속 수금·증빙 확인이 필요합니다.',
      requirementCount: 1,
    };
  }

  if (handlerRef === 'workbench:intent:process_new_order' || actionId === 'order:create') {
    return {
      state: 'required',
      label: '주문 증빙 필요',
      detail: '납품·승인 증빙 확인이 필요합니다.',
      requirementCount: 1,
    };
  }

  if (actionId === 'drag_drop:create_purchase_request') {
    return {
      state: 'required',
      label: '구매 증빙 필요',
      detail: '구매 요청 승인·증빙 확인이 필요합니다.',
      requirementCount: 1,
    };
  }

  if (actionId === 'drag_drop:assign_task') {
    return {
      state: 'attached',
      label: '배정 기록됨',
      detail: '담당자 배정이 기록되었습니다.',
      attachedCount: 1,
    };
  }

  if (actionId === 'drag_drop:start_production_flow') {
    return {
      state: 'required',
      label: '생산 증빙 필요',
      detail: '생산 큐 이동·실행 증빙 확인이 필요합니다.',
      requirementCount: 1,
    };
  }

  if (actionId === 'drag_drop:route_to_picking_queue') {
    return {
      state: 'required',
      label: '피킹 증빙 필요',
      detail: '피킹 큐 배정·출고 증빙 확인이 필요합니다.',
      requirementCount: 1,
    };
  }

  if (actionId === 'drag_drop:route_to_collection_queue') {
    return {
      state: 'required',
      label: '수금 증빙 필요',
      detail: '수금 큐 후속·증빙 확인이 필요합니다.',
      requirementCount: 1,
    };
  }

  if (actionId === 'drag_drop:attach_approval_packet') {
    return {
      state: 'attached',
      label: '승인 패킷 첨부됨',
      detail: '승인 패킷 증빙 흐름이 연결되었습니다.',
      attachedCount: 1,
    };
  }

  if (actionId === 'start_assignment_workflow') {
    return {
      state: 'required',
      label: '업무 증빙 필요',
      detail: '배정 워크플로우 증빙 확인이 필요합니다.',
      requirementCount: 1,
    };
  }

  if (actionId === 'start_inventory_workflow') {
    return {
      state: 'required',
      label: '재고 증빙 필요',
      detail: '입고·이동 증빙 확인이 필요합니다.',
      requirementCount: 1,
    };
  }

  return undefined;
}

export function resolveAuxiliaryEvidenceDisplayState(input: {
  exceptions: readonly WorkbenchException[];
  actionEntries?: readonly WorkbenchActionExecutionEntry[];
  primaryAttachmentCount?: number;
  pendingTaskRequirementCount?: number;
  serverSummaryCounts?: WorkbenchAuxiliarySummary['counts'];
}): AuxiliaryEvidenceDisplayState {
  const missingExceptions = input.exceptions.filter(
    (exception) => exception.category === 'missing_evidence'
  );
  const missingCount = missingExceptions.length + (input.pendingTaskRequirementCount ?? 0);

  let attachedCount = input.primaryAttachmentCount ?? 0;
  let requiredCount = 0;
  let serverBacked = false;

  const serverEvidence = input.serverSummaryCounts?.evidence;
  if (typeof serverEvidence === 'number' && serverEvidence > 0) {
    attachedCount = Math.max(attachedCount, serverEvidence);
    serverBacked = true;
  }

  for (const entry of input.actionEntries ?? []) {
    if (!entry.evidenceState) {
      continue;
    }
    serverBacked = true;
    switch (entry.evidenceState.state) {
      case 'attached':
        attachedCount = Math.max(attachedCount, entry.evidenceState.attachedCount ?? 1);
        break;
      case 'missing':
        requiredCount += entry.evidenceState.requirementCount ?? 1;
        break;
      case 'required':
        requiredCount += entry.evidenceState.requirementCount ?? 1;
        break;
      default:
        break;
    }
  }

  if (missingCount > 0) {
    requiredCount = Math.max(requiredCount, missingCount);
  }

  return {
    requiredCount,
    missingCount,
    attachedCount,
    serverBacked,
  };
}

export function buildEvidenceStateBucketRecords(input: {
  exceptions: readonly WorkbenchException[];
  actionEntries?: readonly WorkbenchActionExecutionEntry[];
  primaryAttachmentCount?: number;
  pendingTaskRequirementCount?: number;
  serverSummaryCounts?: WorkbenchAuxiliarySummary['counts'];
}): Array<{ id: string; label: string; detail?: string }> {
  const display = resolveAuxiliaryEvidenceDisplayState(input);
  const records: Array<{ id: string; label: string; detail?: string }> = [];

  if (display.requiredCount > 0) {
    records.push({
      id: 'evidence-required',
      label: '필수 증빙',
      detail: `필수 ${display.requiredCount}건`,
    });
  }
  if (display.missingCount > 0) {
    records.push({
      id: 'evidence-missing',
      label: '증빙 누락',
      detail: `미충족 ${display.missingCount}건`,
    });
  }
  if (display.attachedCount > 0) {
    records.push({
      id: 'evidence-attached',
      label: '첨부 증빙',
      detail: display.serverBacked
        ? `서버·작업 메타 ${display.attachedCount}건`
        : `첨부 ${display.attachedCount}건`,
    });
  }

  const evidenceExceptions = input.exceptions.filter(
    (exception) => exception.category === 'missing_evidence'
  );
  for (const exception of evidenceExceptions) {
    records.push({
      id: `evidence-${exception.id}`,
      label: exception.title,
      detail: exception.message ?? '증빙 보강 필요',
    });
  }

  for (const entry of input.actionEntries ?? []) {
    if (!entry.evidenceState) {
      continue;
    }
    records.push({
      id: `evidence-action-${entry.id}`,
      label: entry.evidenceState.label,
      ...(entry.evidenceState.detail ? { detail: entry.evidenceState.detail } : {}),
    });
  }

  if ((input.primaryAttachmentCount ?? 0) > 0) {
    records.push({
      id: 'evidence-primary-attachments',
      label: '첨부 자료',
      detail: `Primary 리소스 ${input.primaryAttachmentCount}건`,
    });
  }

  return records;
}
